import React, {
  memo,
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useDropdown } from '../dropdown.context';
import {
  PortalContent,
  InlineContent,
  InlineWrapper,
} from '../dropdown.styled';
import { Side, Align, PortalMode } from '../dropdown.types';
import { useDropdownPosition } from '../use-dropdown-position';

type ContentProps = {
  side?: Side;
  align?: Align;
  mode?: PortalMode;
  modal?: boolean;
  style?: React.CSSProperties;
  className?: string;
};

export const Content = memo(
  ({
    children,
    side = 'bottom',
    align = 'start',
    mode = 'portal',
    modal = false,
    style,
    className,
  }: PropsWithChildren<ContentProps>) => {
    const { open, triggerRef, contentRef, close } = useDropdown();
    const [visible, setVisible] = useState<boolean>(false);
    const [closing, setClosing] = useState<boolean>(false);
    const itemRefs = useRef<HTMLElement[]>([]);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    useEffect(() => {
      if (open) {
        setVisible(true);
        setClosing(false);
        setFocusedIndex(-1);
      } else if (visible) {
        setClosing(true);
        const t = setTimeout(() => {
          setVisible(false);
          setClosing(false);
        }, 150);

        return () => clearTimeout(t);
      }
    }, [open, visible]);

    useEffect(() => {
      if (!visible || !modal) {
        return;
      }

      const backdrop = document.createElement('div');
      backdrop.style.cssText = 'position:fixed;inset:0;z-index:9999;';
      document.body.appendChild(backdrop);

      const handler = (e: PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        close();
      };

      backdrop.addEventListener('pointerdown', handler, { capture: true });

      return () => {
        backdrop.removeEventListener('pointerdown', handler, { capture: true });
        document.body.removeChild(backdrop);
      };
    }, [visible, modal, close]);

    const pos = useDropdownPosition(
      triggerRef,
      contentRef,
      side,
      align,
      visible,
      mode === 'portal'
    );

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      const items = itemRefs.current.filter(Boolean);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((i) => Math.min(i + 1, items.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(items.length - 1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          items[focusedIndex]?.click();
          break;
        default:
          break;
      }
    };

    useEffect(() => {
      itemRefs.current[focusedIndex]?.focus();
    }, [focusedIndex]);

    if (!visible) {
      return null;
    }

    const contentProps = {
      ref: contentRef,
      'data-state': closing ? 'closed' : 'open',
      'data-side': side,
      role: 'menu' as const,
      tabIndex: -1,
      onKeyDown,
      style: { ...pos, zIndex: 10000, ...style },
      className,
    };

    const wrappedChildren = React.Children.map(children, (child, i) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      return React.cloneElement(child as React.ReactElement, {
        _ref: (el: HTMLElement | null) => {
          if (el) {
            itemRefs.current[i] = el;
          }
        },
        _focused: focusedIndex === i,
      });
    });

    const content =
      mode === 'portal' ? (
        <PortalContent {...contentProps}>{wrappedChildren}</PortalContent>
      ) : (
        <InlineContent {...contentProps}>{wrappedChildren}</InlineContent>
      );

    if (mode === 'portal') {
      return createPortal(content, document.body);
    }

    return <InlineWrapper>{content}</InlineWrapper>;
  }
);

Content.displayName = 'Dropdown.Content';
